import React, { useEffect, useRef, useState } from "react";
import {
  listReviewLinks,
  revokeReviewLink,
  ReviewLinkSummary,
} from "../../services/postService";
import { useToast } from "../../context/ToastContext";
import ConfirmDialog from "../Shared/ConfirmDialog";

interface ReviewLinksPanelProps {
  customerId: string | null;
  idToken: string;
  /** Only `review_link_id` is read. */
  posts: { review_link_id?: string }[];
}

const STATUS_STYLES: Record<ReviewLinkSummary["status"], string> = {
  ACTIVE: "bg-green-100 text-green-800",
  EXPIRED: "bg-gray-100 text-gray-700",
  REVOKED: "bg-red-100 text-red-800",
  EMPTY: "bg-gray-100 text-gray-700",
};

const formatDate = (iso?: string) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "-";

/**
 * Lists the review links shared for a customer, with revoke (SGL-389).
 *
 * Renders nothing unless a loaded post carries `review_link_id`. That field is
 * the proof the backend has the /review-links routes: an older backend 403s
 * unknown routes and the global axios interceptor treats a 403 as a logout.
 * The list is fetched on first expand only.
 */
const ReviewLinksPanel: React.FC<ReviewLinksPanelProps> = ({
  customerId,
  idToken,
  posts,
}) => {
  const [open, setOpen] = useState(false);
  const [links, setLinks] = useState<ReviewLinkSummary[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<ReviewLinkSummary | null>(null);
  const { showToast } = useToast();

  // A Brand switch must not keep showing (or revoking) the previous Brand's links.
  // Async results are applied only if no switch happened since they started.
  const generation = useRef(0);
  useEffect(() => {
    generation.current += 1;
    setOpen(false);
    setLinks(null);
    setLoading(false);
    setError(null);
    setRevokingId(null);
    setPendingRevoke(null);
  }, [customerId]);

  const backendSupportsLinks = posts.some((p) => !!p.review_link_id);
  if (!customerId || !backendSupportsLinks) return null;

  const load = async () => {
    const gen = generation.current;
    setLoading(true);
    setError(null);
    try {
      const fetched = await listReviewLinks(customerId, idToken);
      if (generation.current === gen) setLinks(fetched);
    } catch {
      if (generation.current === gen) setError("Could not load review links.");
    } finally {
      if (generation.current === gen) setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && links === null && !loading) void load();
  };

  const handleRevoke = async (link: ReviewLinkSummary) => {
    const gen = generation.current;
    setRevokingId(link.link_id);
    try {
      await revokeReviewLink(customerId, link.link_id, idToken);
      if (generation.current === gen) {
        setLinks((prev) =>
          (prev ?? []).map((l) =>
            l.link_id === link.link_id ? { ...l, status: "REVOKED" } : l
          )
        );
      }
    } catch {
      if (generation.current === gen) {
        showToast("Failed to revoke review link. Please try again.", "error");
      }
    } finally {
      if (generation.current === gen) {
        setRevokingId(null);
        setPendingRevoke(null);
      }
    }
  };

  return (
    <div className="mb-4 rounded-md border border-theme-border bg-theme-background">
      <button
        onClick={toggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between px-4 py-2 text-left text-sm font-medium text-theme-fg"
      >
        <span>Shared review links</span>
        <span aria-hidden="true">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="border-t border-theme-border p-4">
          {loading && (
            <p className="text-sm text-theme-fg-muted">Loading links...</p>
          )}
          {error && (
            <p className="text-sm text-red-600">
              {error}{" "}
              <button className="underline" onClick={load}>
                Retry
              </button>
            </p>
          )}
          {links && links.length === 0 && !loading && (
            <p className="text-sm text-theme-fg-muted">No review links yet.</p>
          )}
          {links && links.length > 0 && (
            <ul className="space-y-2">
              {links.map((link) => (
                <li
                  key={link.link_id}
                  className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded border border-theme-border p-2 text-sm text-theme-fg"
                >
                  <span
                    className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[link.status]}`}
                  >
                    {link.status}
                  </span>
                  <span>Created {formatDate(link.created_at)}</span>
                  <span>Expires {formatDate(link.expires_at)}</span>
                  <span>
                    {link.post_count} {link.post_count === 1 ? "post" : "posts"}
                  </span>
                  <span className="ml-auto flex items-center gap-2">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(link.url);
                        alert("Link copied to clipboard!");
                      }}
                      className="rounded bg-surface-ui p-1 hover:bg-surface-ui-hover"
                      title="Copy link"
                    >
                      📋
                    </button>
                    {link.status === "ACTIVE" && (
                      <button
                        onClick={() => setPendingRevoke(link)}
                        disabled={revokingId === link.link_id}
                        className="rounded-md border border-theme-border bg-surface-ui px-3 py-1 text-sm transition-colors hover:bg-surface-ui-hover disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {revokingId === link.link_id ? "Revoking..." : "Revoke"}
                      </button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <ConfirmDialog
        isOpen={pendingRevoke !== null}
        title="Revoke this review link?"
        message="Anyone with the link will no longer be able to view it."
        confirmLabel="Revoke link"
        destructive
        isConfirming={revokingId !== null}
        onConfirm={() => {
          if (pendingRevoke) void handleRevoke(pendingRevoke);
        }}
        onCancel={() => setPendingRevoke(null)}
      />
    </div>
  );
};

export default ReviewLinksPanel;
